import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Check, X, Pencil, Plus, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { DeepReadOverlay, DeepReadPanel } from "@/components/DeepReadOverlay";

const BASIS_STYLE = {
  EXPLICIT: "bg-emerald-100 text-emerald-800",
  GEOMETRICALLY_REQUIRED: "bg-sky-100 text-sky-800",
  PROCESS_INFERRED: "bg-amber-100 text-amber-800",
  NOT_REQUIRED: "bg-zinc-100 text-zinc-500",
  UNKNOWN: "bg-red-100 text-red-800",
};
const VOICE_STYLE = {
  PRICED: "bg-emerald-100 text-emerald-800",
  INCOMPLETE: "bg-amber-100 text-amber-800",
  NOT_APPLICABLE: "bg-zinc-100 text-zinc-500",
};

const FieldRow = ({ fieldKey, field, gtValue, correction, onAction }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const status = correction?.action;
  return (
    <div className="border-b border-border py-2" data-testid={`verify-field-${fieldKey}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs w-36 shrink-0 text-muted-foreground">{fieldKey}</span>
        <span className="text-sm font-medium" data-testid={`verify-field-value-${fieldKey}`}>
          {correction?.action === "correct" || correction?.action === "add"
            ? `${correction.value} (corretto)` : String(field?.value ?? "—")}
          {field?.unit ? ` ${field.unit}` : ""}
        </span>
        {gtValue != null && (
          <span className="text-[11px] text-sky-800 bg-sky-50 px-1.5 py-0.5" data-testid={`gt-value-${fieldKey}`}>
            Excel: {gtValue}
          </span>
        )}
        {status && (
          <Badge className={status === "reject" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}
            data-testid={`verify-field-status-${fieldKey}`}>{status}</Badge>
        )}
        <span className="ml-auto flex gap-1 items-center">
          <Button size="sm" variant="outline" className="h-6 px-2" title="Conferma"
            data-testid={`confirm-${fieldKey}`}
            onClick={() => onAction(fieldKey, "confirm", null, field?.value)}>
            <Check size={12} />
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2" title="Correggi"
            data-testid={`edit-${fieldKey}`} onClick={() => setEditing(!editing)}>
            <Pencil size={12} />
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2" title="Rifiuta"
            data-testid={`reject-${fieldKey}`}
            onClick={() => onAction(fieldKey, "reject", null, field?.value)}>
            <X size={12} />
          </Button>
          <select className="h-6 border border-border text-[10px] bg-background" defaultValue=""
            data-testid={`classify-${fieldKey}`}
            onChange={(e) => { if (e.target.value) { onAction(fieldKey, e.target.value, null, field?.value); e.target.value = ""; } }}>
            <option value="">stato…</option>
            <option value="missing">MISSING</option>
            <option value="unreadable">UNREADABLE</option>
            <option value="not_applicable">NOT_APPLICABLE</option>
          </select>
        </span>
      </div>
      {field?.evidence?.text && (
        <div className="text-[10px] text-muted-foreground font-mono mt-1 truncate"
          title={field.evidence.text}>
          evidenza: {field.evidence.text.slice(0, 90)} · {field.origin} · p.{field.page} · conf {field.confidence}
        </div>
      )}
      {editing && (
        <div className="flex gap-2 mt-1">
          <Input className="h-7 text-xs" value={val} onChange={(e) => setVal(e.target.value)}
            placeholder="nuovo valore" data-testid={`edit-input-${fieldKey}`} />
          <Button size="sm" className="h-7" data-testid={`edit-save-${fieldKey}`}
            onClick={() => { onAction(fieldKey, "correct", val, field?.value); setEditing(false); }}>
            Salva
          </Button>
        </div>
      )}
    </div>
  );
};

export default function PdfVerification() {
  const { jobId } = useParams();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageUrl, setPageUrl] = useState(null);
  const [addKey, setAddKey] = useState("");
  const [addVal, setAddVal] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pdf-jobs/${jobId}/verification`);
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento verifica");
    }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let revoke;
    (async () => {
      try {
        const res = await api.get(`/pdf-jobs/${jobId}/page/${page}?dpi=110`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        setPageUrl(url); revoke = url;
      } catch { setPageUrl(null); }
    })();
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [jobId, page]);

  const onAction = async (fieldKey, action, newValue, originalValue) => {
    try {
      await api.post(`/pdf-jobs/${jobId}/verification/values`,
        { field_key: fieldKey, action, new_value: newValue, original_value: originalValue });
      toast.success(`${fieldKey}: ${action} registrato`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const onFeatureAction = async (payload) => {
    try {
      await api.post(`/pdf-jobs/${jobId}/verification/features`, payload);
      toast.success(`${payload.feature_id}: ${payload.action} registrato`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio feature");
    }
  };

  const recalc = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pdf-jobs/${jobId}/verification/recalculate`);
      toast.success("Preventivo ricalcolato");
      setData((d) => ({ ...d, quote: r.quote, development: r.development, last_recalculated_at: r.recalculated_at }));
    } catch { toast.error("Errore ricalcolo"); }
    setBusy(false);
  };

  if (!data) return <div className="p-8 text-sm text-muted-foreground" data-testid="verify-loading">Caricamento…</div>;
  const q = data.quote || {};
  const totals = q.totals || {};
  const nav = data.queue_nav;
  const ind = data.indicators || {};
  const pick = (f) => (f && (f.normalized ?? f.value) != null ? `${f.normalized ?? f.value} [${f.status}]` : null);
  const gtCase = (data.gt_reference || {}).case || {};
  const gtComp = ((data.gt_reference || {}).components || [])[0] || {};
  const gtMap = {
    drawing_number: pick(gtCase.codice_disegno),
    drawing_title: pick(gtCase.descrizione_tecnica_breve),
    revision: pick(gtCase.revisione),
    quantity: pick(gtCase.quantit_richiesta),
    scale: pick(gtCase.scala),
    material: pick(gtComp.materiale),
    material_grade: pick(gtComp.materiale),
    thickness_mm: pick(gtComp.spessore_mm),
  };

  return (
    <div className="p-4 space-y-4" data-testid="pdf-verification-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/verification-queue" className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="back-to-queue">
          <ArrowLeft size={14} /> Coda
        </Link>
        {data.rfq_id && (
          <Link to={`/rfqs/${data.rfq_id}`} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="back-to-rfq">
            <ArrowLeft size={14} /> RFQ
          </Link>)}
        <h1 className="text-lg font-semibold">Verifica {nav?.case_id || "PDF"} <span className="font-mono text-xs text-muted-foreground">{jobId}</span></h1>
        <Badge data-testid="verify-reliability">{q.reliability}</Badge>
        {ind.completed
          ? <Badge className="bg-emerald-100 text-emerald-800" data-testid="case-completed">CASO COMPLETATO</Badge>
          : <Badge className="bg-amber-100 text-amber-800" data-testid="case-pending">{ind.handled}/{ind.required_total} approvati</Badge>}
        {q.estimate_label && (
          <Badge className="bg-amber-100 text-amber-800" data-testid="verify-estimate-label">{q.estimate_label}</Badge>)}
        <span className="ml-auto flex gap-2 items-center">
          <Link to={`/pdf-jobs/${jobId}/expert`} data-testid="open-expert-quote-link"
            className="text-xs font-medium border border-border px-2 py-1.5 hover:bg-accent">
            Preventivo esperto →
          </Link>
          {nav?.prev_job_id && (
            <Button size="sm" variant="outline" data-testid="queue-prev-case"
              onClick={() => { window.location.href = `/pdf-jobs/${nav.prev_job_id}/verify`; }}>
              <ChevronLeft size={13} /> Prec.
            </Button>)}
          {nav?.next_job_id && (
            <Button size="sm" variant="outline" data-testid="queue-next-case"
              onClick={() => { window.location.href = `/pdf-jobs/${nav.next_job_id}/verify`; }}>
              Succ. <ChevronRight size={13} />
            </Button>)}
          <Button size="sm" onClick={recalc} disabled={busy} data-testid="recalculate-btn">
            <RefreshCw size={13} className="mr-1" /> Ricalcola
          </Button>
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ── PDF ── */}
        <Card data-testid="verify-pdf-panel">
          <CardHeader className="py-2 flex-row items-center justify-between">
            <CardTitle className="text-sm">Disegno</CardTitle>
            <div className="flex items-center gap-2 text-xs">
              <Button size="sm" variant="outline" className="h-6 px-1" onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev"><ChevronLeft size={12} /></Button>
              <span>pag. {page}</span>
              <Button size="sm" variant="outline" className="h-6 px-1" onClick={() => setPage((p) => p + 1)} data-testid="page-next"><ChevronRight size={12} /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {data.deep_read ? (
              <DeepReadOverlay deepRead={data.deep_read} page={page} pageUrl={pageUrl}
                onFeatureAction={onFeatureAction} featureReviews={data.feature_reviews} />
            ) : (pageUrl ? <img src={pageUrl} alt="pagina disegno" className="w-full border border-border" data-testid="pdf-page-image" />
              : <div className="text-xs text-muted-foreground">pagina non disponibile</div>)}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* ── Lettura profonda V2 ── */}
          {data.deep_read && (
            <Card data-testid="verify-deepread-panel">
              <CardHeader className="py-2"><CardTitle className="text-sm">Lettura profonda del disegno (V2)</CardTitle></CardHeader>
              <CardContent>
                <DeepReadPanel deepRead={data.deep_read} onFeatureAction={onFeatureAction} />
              </CardContent>
            </Card>
          )}
          {/* ── Dati estratti ── */}
          <Card data-testid="verify-fields-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Dati estratti (conferma / correggi / rifiuta)</CardTitle></CardHeader>
            <CardContent>
              {Object.entries(data.fields || {}).map(([k, f]) => (
                <FieldRow key={k} fieldKey={k} field={f} gtValue={gtMap[k]} correction={data.corrections?.[k]} onAction={onAction} />
              ))}
              <div className="flex gap-2 mt-3 items-center">
                <select className="h-7 border border-border text-xs px-1 bg-background" value={addKey}
                  onChange={(e) => setAddKey(e.target.value)} data-testid="add-key-select">
                  <option value="">— aggiungi dato mancante —</option>
                  {(data.allowed_keys || []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <Input className="h-7 text-xs w-32" placeholder="valore" value={addVal}
                  onChange={(e) => setAddVal(e.target.value)} data-testid="add-value-input" />
                <Button size="sm" className="h-7" disabled={!addKey || !addVal} data-testid="add-value-btn"
                  onClick={() => { onAction(addKey, "add", addVal, null); setAddKey(""); setAddVal(""); }}>
                  <Plus size={12} className="mr-1" />Aggiungi
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ── Feature ── */}
          <Card data-testid="verify-features-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Feature ({(data.normalized?.features || []).length})</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {(data.normalized?.features || []).map((f, i) => (
                <div key={i} className="text-xs flex items-center gap-2" data-testid={`feature-row-${i}`}>
                  <span className="font-mono">{f.feature_type}</span>
                  <span>qty {f.quantity}</span>
                  {f.original_text && <span className="text-muted-foreground truncate">“{f.original_text}”</span>}
                  {f.status === "needs_review" && <Badge className="bg-amber-100 text-amber-800">da verificare</Badge>}
                </div>
              ))}
              {!(data.normalized?.features || []).length && <div className="text-xs text-muted-foreground">nessuna feature rilevata</div>}
            </CardContent>
          </Card>

          {/* ── Sezioni da approvare ── */}
          <Card data-testid="verify-sections-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Sezioni tecniche da approvare</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {(data.required_items || []).filter((k) => k.endsWith("_review") || k === "semilavorato" || k === "overall_dimensions" || k === "drawing_kind").map((k) => {
                const c = data.corrections?.[k];
                return (
                  <div key={k} className="text-xs flex items-center gap-2" data-testid={`section-${k}`}>
                    <span className="w-44 font-mono">{k}</span>
                    {c ? <Badge className="bg-emerald-100 text-emerald-800" data-testid={`section-status-${k}`}>{c.action}</Badge>
                      : <Badge className="bg-amber-100 text-amber-800">da approvare</Badge>}
                    <span className="ml-auto flex gap-1">
                      <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`section-approve-${k}`}
                        onClick={() => onAction(k, "confirm", null, null)}>Approva</Button>
                      <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`section-na-${k}`}
                        onClick={() => onAction(k, "not_applicable", null, null)}>N/A</Button>
                    </span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* ── Distinta (assiemi) ── */}
          {data.assembly && (
            <Card data-testid="verify-assembly-panel">
              <CardHeader className="py-2"><CardTitle className="text-sm">Distinta ({data.assembly.n_components} componenti — PROPOSTA da confermare)</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {(data.assembly.components || []).map((c, i) => (
                  <div key={i} className="text-xs flex gap-2 flex-wrap" data-testid={`assembly-comp-${i}`}>
                    <span className="font-mono">{c.codice_pezzo || c.id_componente}</span>
                    <span>{c.descrizione}</span>
                    <span className="text-muted-foreground">{c.materiale} {c.spessore_mm ? `sp.${c.spessore_mm}` : ""}</span>
                    <span>qty/assieme: {c.qty_per_assieme ?? "?"}</span>
                    <span>totale: {c.qty_totale_da_produrre ?? "da definire"}</span>
                    <Badge className="bg-amber-100 text-amber-800">{c.status}</Badge>
                  </div>
                ))}
                <div className="text-[10px] text-muted-foreground">{data.assembly.anti_double_count}</div>
              </CardContent>
            </Card>
          )}

          {/* ── Proposte vision da confermare ── */}
          {(data.vision_treatment_suggestions || []).length > 0 && (
            <Card data-testid="verify-suggestions-panel">
              <CardHeader className="py-2"><CardTitle className="text-sm">Trattamenti letti via vision (da confermare)</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {data.vision_treatment_suggestions.map((sg, i) => (
                  <div key={i} className="text-xs flex items-center gap-2" data-testid={`suggestion-${sg.treatment}`}>
                    <span className="font-mono">{sg.treatment}</span>
                    <span className="text-muted-foreground truncate">“{sg.original_text}”</span>
                    <Badge className="bg-amber-100 text-amber-800">solo vision</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ── Ciclo produttivo ── */}
          <Card data-testid="verify-cycle-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Ciclo produttivo motivato</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {(data.cycle?.steps || []).filter((s) => s.basis !== "NOT_REQUIRED").map((s, i) => (
                <div key={i} className="text-xs flex items-center gap-2" data-testid={`cycle-step-${s.process}`}>
                  <span className="w-40">{s.label_it}</span>
                  <Badge className={BASIS_STYLE[s.basis] || ""}>{s.basis}</Badge>
                  {s.qty ? <span>qty {s.qty}</span> : null}
                  {s.evidence && <span className="text-muted-foreground truncate" title={s.evidence}>{s.evidence.slice(0, 50)}</span>}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ── Sviluppo ── */}
          <Card data-testid="verify-development-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Sviluppo</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1">
              <div>Affidabilità: <Badge data-testid="dev-reliability">{data.development?.reliability}</Badge></div>
              {data.development?.development && (
                <pre className="bg-muted p-2 text-[10px] overflow-auto max-h-32" data-testid="dev-detail">{JSON.stringify(data.development.development, null, 1)}</pre>)}
              {(data.development?.missing_inputs || []).length > 0 && (
                <div className="text-amber-700" data-testid="dev-missing">Dati mancanti: {data.development.missing_inputs.join(", ")}</div>)}
            </CardContent>
          </Card>

          {/* ── Preventivo ── */}
          <Card data-testid="verify-quote-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Preventivo trasparente
              <span className="ml-2 text-[10px] text-muted-foreground">compl. tecnica {q.completeness?.technical_pct}% · economica {q.completeness?.economic_pct}%</span>
            </CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {(q.voices || []).map((v, i) => (
                <div key={i} className="text-xs flex items-center gap-2" data-testid={`quote-voice-${v.code}`}>
                  <span className="w-52 truncate">{v.label}</span>
                  <Badge className={VOICE_STYLE[v.status] || ""}>{v.status}</Badge>
                  <span className="font-mono">{v.amount_eur != null ? `€ ${v.amount_eur.toFixed(4)}` : "—"}</span>
                  {v.operator_question && <span className="text-amber-700 truncate" title={v.operator_question}>{v.operator_question.slice(0, 45)}…</span>}
                </div>
              ))}
              <div className="border-t border-border pt-2 mt-2 text-xs space-y-1">
                <div data-testid="quote-total-laser">Costo industriale laser: {totals.industrial_cost_laser_eur != null ? `€ ${totals.industrial_cost_laser_eur.toFixed(4)}` : "—"}</div>
                <div data-testid="quote-total-punch">Costo industriale punzone: {totals.industrial_cost_punch_eur != null ? `€ ${totals.industrial_cost_punch_eur.toFixed(4)}` : "—"}</div>
                <div data-testid="quote-definitive">Prezzo definitivo: {totals.definitive ? `SÌ — € ${totals.total_price_eur}` : "NO"}</div>
                {!totals.definitive && (
                  <div className="text-amber-700" data-testid="quote-blocked-reasons">
                    Perché non definitivo: {(data.why_not_definitive || []).join("; ") || "voci incomplete"}
                  </div>)}
              </div>
              {(q.operator_questions || []).length > 0 && (
                <div className="mt-2" data-testid="operator-questions">
                  <div className="text-xs font-semibold">Domande per l'operatore</div>
                  {(q.operator_questions || []).map((x, i) => (
                    <div key={i} className="text-[11px] text-muted-foreground" data-testid={`question-${x.key}`}>• {x.question}</div>))}
                </div>)}
            </CardContent>
          </Card>

          {/* ── Audit trail ── */}
          <Card data-testid="verify-audit-panel">
            <CardHeader className="py-2"><CardTitle className="text-sm">Audit trail correzioni ({(data.audit_trail || []).length}) · versioni GT: {(data.versions || []).length}</CardTitle></CardHeader>
            <CardContent className="space-y-1 max-h-48 overflow-auto">
              {(data.audit_trail || []).map((a, i) => (
                <div key={i} className="text-[11px] font-mono" data-testid={`audit-row-${i}`}>
                  {a.at} · {a.user_email} · {a.field_key} · {a.action} · {String(a.original_value)} → {String(a.value)}
                </div>
              ))}
              {!(data.audit_trail || []).length && <div className="text-xs text-muted-foreground">nessuna correzione registrata</div>}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
